import React, { useEffect, useState } from "react";
import { api } from "../api";
import {
  RefreshCw, Database, AlertCircle, CheckCircle2,
  Calendar, ExternalLink, ShieldCheck,
} from "lucide-react";

function timeAgo(value) {
  if (!value) return "—";
  try {
    const ms = Date.parse(value);
    if (!ms || isNaN(ms)) return "—";
    const diff = Math.floor((Date.now() - ms) / 1000);
    if (diff < 60)    return "just now";
    if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return "—"; }
}

function fmtDateTime(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

export default function AdminJobsSync() {
  const [stats, setStats] = useState(null);
  const [runs, setRuns] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");

  const loadAll = async () => {
    setError("");
    try {
      const [s, r] = await Promise.all([
        api.get("/jobs/external/stats"),
        api.get("/admin/jobs/runs", { params: { limit: 20 } }),
      ]);
      setStats(s.data); setRuns(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load sync state.");
    }
  };

  useEffect(() => { loadAll(); }, []);

  const syncNow = async () => {
    if (syncing) return;
    setSyncing(true); setError("");
    try {
      await api.post("/admin/jobs/sync");
      await loadAll();
    } catch (e) {
      setError(e?.response?.data?.detail || "Sync failed.");
    } finally {
      setSyncing(false);
    }
  };

  const last = runs[0] || stats?.last_run || null;

  return (
    <div className="space-y-6" data-testid="admin-jobs-sync">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="eyebrow" style={{ color: "var(--signal)" }}>CRM · External jobs</span>
          <h1 className="font-display font-black text-3xl md:text-4xl mt-1">Job crawler console</h1>
          <p className="text-muted text-sm mt-1">
            Background job runs every 6 hours and pulls from RemoteOK, Adzuna, and Jooble.
            Trigger a manual sync any time.
          </p>
        </div>
        <button onClick={syncNow} disabled={syncing} className="btn-primary"
          data-testid="sync-now-btn">
          <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
          {syncing ? "Syncing…" : "Sync now"}
        </button>
      </div>

      {error && (
        <div className="card card-pad inline-flex items-center gap-2"
          style={{ borderColor: "#DC2626", color: "#DC2626" }}>
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* KPIs */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Total external jobs"
             value={stats?.total ?? "—"}
             icon={Database}
             color="#006CFF"
             testid="kpi-total" />
        <Kpi label="Sources active"
             value={`${(stats?.by_source || []).length}/3`}
             icon={ShieldCheck}
             color="#0040A8"
             testid="kpi-sources" />
        <Kpi label="CoEs matched"
             value={`${(stats?.by_coe || []).length}/10`}
             icon={CheckCircle2}
             color="#9933FF"
             testid="kpi-coes" />
        <Kpi label="Last sync"
             value={last ? timeAgo(last.synced_at) : "Never"}
             icon={Calendar}
             color="#0099FF"
             testid="kpi-last" />
      </div>

      {/* Sources & CoE breakdown */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card card-pad">
          <div className="text-xs uppercase tracking-widest font-bold text-muted">By source</div>
          <table className="dense w-full mt-3">
            <thead>
              <tr>
                <th className="text-left">Source</th>
                <th className="text-right">Jobs</th>
                <th className="text-right">Last synced</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.by_source || []).map(s => (
                <tr key={s.source} data-testid={`source-row-${s.source}`}>
                  <td className="font-mono uppercase tracking-wider text-xs">{s.source}</td>
                  <td className="text-right font-bold">{s.count}</td>
                  <td className="text-right text-xs text-muted">{timeAgo(s.last_synced)}</td>
                </tr>
              ))}
              {!stats?.by_source?.length && (
                <tr><td colSpan={3} className="text-muted text-sm py-3">No sources active — run a sync.</td></tr>
              )}
            </tbody>
          </table>
          <p className="text-[11px] text-muted mt-3">
            Adzuna / Jooble require API keys in <code>backend/.env</code> (<code>ADZUNA_APP_ID</code>, <code>ADZUNA_APP_KEY</code>, <code>JOOBLE_API_KEY</code>).
          </p>
        </div>

        <div className="card card-pad">
          <div className="text-xs uppercase tracking-widest font-bold text-muted">By Center of Excellence</div>
          <div className="mt-3 space-y-2">
            {(stats?.by_coe || []).map(c => {
              const max = Math.max(1, ...(stats?.by_coe || []).map(x => x.count));
              const pct = Math.round((c.count / max) * 100);
              return (
                <div key={c.coe} data-testid={`coe-row-${c.coe}`}>
                  <div className="flex justify-between text-xs font-medium">
                    <span className="font-mono uppercase tracking-wider">{c.coe}</span>
                    <span>{c.count}</span>
                  </div>
                  <div className="h-1.5 mt-1 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full transition-all"
                      style={{ width: `${pct}%`, background: "var(--signal)" }} />
                  </div>
                </div>
              );
            })}
            {!stats?.by_coe?.length && (
              <div className="text-muted text-sm">No CoE matches yet.</div>
            )}
          </div>
        </div>
      </div>

      {/* Recent runs */}
      <div className="card card-pad">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest font-bold text-muted">Recent runs · last {runs.length}</div>
          <a href="/api/jobs/external" target="_blank" rel="noopener noreferrer"
            className="text-xs text-signal font-semibold inline-flex items-center gap-1 hover:underline">
            View raw feed <ExternalLink size={11} />
          </a>
        </div>
        <table className="dense w-full mt-3">
          <thead>
            <tr>
              <th className="text-left">When</th>
              <th className="text-right">Pulled</th>
              <th className="text-right">Kept</th>
              <th className="text-right">Skipped</th>
              <th className="text-right">RemoteOK</th>
              <th className="text-right">Adzuna</th>
              <th className="text-right">Jooble</th>
              <th className="text-right">Time</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r, i) => (
              <tr key={r.id || r.synced_at || `run-${i}`} data-testid={`run-row-${i}`}>
                <td className="text-xs text-muted">{fmtDateTime(r.synced_at)}</td>
                <td className="text-right">{r.total_pulled ?? "—"}</td>
                <td className="text-right font-bold text-signal">{r.kept ?? "—"}</td>
                <td className="text-right text-muted">{r.skipped ?? "—"}</td>
                <td className="text-right">{r.remoteok ?? 0}</td>
                <td className="text-right">{r.adzuna ?? 0}</td>
                <td className="text-right">{r.jooble ?? 0}</td>
                <td className="text-right text-xs text-muted">{r.duration_seconds ? `${r.duration_seconds}s` : "—"}</td>
              </tr>
            ))}
            {!runs.length && (
              <tr><td colSpan={8} className="text-muted text-sm py-3 text-center">No runs yet — click &quot;Sync now&quot; to start the first one.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="card card-pad" data-testid={testid}>
      <div className="w-10 h-10 rounded-lg grid place-items-center"
        style={{ background: `${color}15`, color }}><Icon size={20} /></div>
      <div className="mt-3 font-display font-black text-2xl tracking-tight" style={{ color }}>{value}</div>
      <div className="text-xs uppercase tracking-widest font-bold text-muted mt-1">{label}</div>
    </div>
  );
}
